import React, { useEffect, useState } from "react";

import { faSparkles, faTrashCan } from "@fortawesome/pro-solid-svg-icons";

import { Skeleton } from "@performyard/py-components";
import { DefaultRephraseResponseStreamJson } from "@performyard/service-contracts";

import Button from "../../Button";
import IconButton from "../../Button/IconButton";
import Icon from "../../Icon/Icon";

const InlineLoader = ({ hasTrashIcon = false, isExpanded = false }: { hasTrashIcon: boolean; isExpanded: boolean }) => {
	return (
		<div className="flex flex-col">
			<Skeleton className="h-4 w-4/12 rounded-none" />
			<div className="my-2 flex flex-col gap-1">
				<Skeleton className="w-12/12 h-4 rounded-none" />
				<Skeleton className="w-12/12 h-4 rounded-none" />
				<Skeleton className="h-4 w-8/12 rounded-none" />
				{isExpanded && (
					<div className="flex flex-col gap-1">
						<Skeleton className="w-12/12 h-4 rounded-none" />
						<Skeleton className="w-12/12 h-4 rounded-none" />
						<Skeleton className="h-4 w-8/12 rounded-none" />
						<Skeleton className="w-12/12 h-4 rounded-none" />
						<Skeleton className="w-12/12 h-4 rounded-none" />
						<Skeleton className="h-4 w-8/12 rounded-none" />
					</div>
				)}
			</div>

			<div className="mt-2 flex gap-1">
				<Skeleton className="h-8 w-[70px] rounded-none" />
				<Skeleton className="h-8 w-[70px] rounded-none" />
				{hasTrashIcon && <Skeleton className="align-end ml-auto size-8 rounded-none" />}
			</div>
		</div>
	);
};

/**
 * RephraseBlockProps
 */
export type RephraseBlockProps = {
	rephrasedData: {
		data: DefaultRephraseResponseStreamJson | string;
		inProgress: boolean;
	};
	onInsert: (newAnswer: string[]) => void;
	onRetry: (retryText: string[]) => void;
	onDismiss: () => void;
	retryDisabled: boolean;
	rephraseOptionsCount: number;
	isExpanded: boolean;
};

export const RephraseBlock = ({
	rephrasedData,
	onInsert,
	onRetry,
	onDismiss,
	rephraseOptionsCount,
	retryDisabled = false,
	isExpanded = false,
}: RephraseBlockProps) => {
	const [newAnswer, setNewAnswer] = useState<string[]>([]);

	useEffect(() => {
		if (rephrasedData.inProgress) return;

		if (typeof rephrasedData.data === "string") {
			setNewAnswer([rephrasedData.data]);
		} else if (Array.isArray(rephrasedData.data.answer)) {
			setNewAnswer(rephrasedData.data.answer);
		} else {
			setNewAnswer([]);
		}
	}, [rephrasedData.data, rephrasedData.inProgress]);

	const handleOnInsert = () => onInsert(newAnswer);
	const handleOnRetry = () => onRetry(newAnswer);

	return (
		<div
			className="group pr-3"
			aria-busy={rephrasedData.inProgress}
			data-state={rephrasedData.inProgress ? "loading" : "ready"}
		>
			<div className="hidden group-data-[state=loading]:block">
				<InlineLoader hasTrashIcon={rephraseOptionsCount > 1} isExpanded={isExpanded} />
			</div>
			<div className="flex flex-col gap-2 py-2 group-data-[state=loading]:hidden">
				<div className="flex items-center gap-1 pb-1">
					<Icon icon={faSparkles} color="primary-5" size="small" />
					<span className="text-paragraph-3 font-semibold text-neutral-900">Rephrase:</span>
				</div>
				<div className="block">
					{newAnswer.length > 0 ? (
						newAnswer.length > 1 ? (
							<ul className="list-inside list-disc whitespace-pre-wrap text-paragraph-3 font-normal text-neutral-900">
								{newAnswer.map((item, index) => (
									<li key={index}>{item}</li>
								))}
							</ul>
						) : (
							<p className="whitespace-pre-wrap text-paragraph-3 font-normal text-neutral-900">
								{newAnswer[0]}
							</p>
						)
					) : (
						<p className="whitespace-pre-wrap text-paragraph-3 font-normal text-neutral-900">
							No response available.
						</p>
					)}
				</div>
			</div>

			<div className="mt-2 flex w-full items-center justify-between group-data-[state=loading]:hidden">
				<div className="flex gap-2">
					<Button
						onClick={handleOnInsert}
						color="blue"
						disabled={rephrasedData.inProgress}
						data-testid="insert-rephrase-button"
					>
						Insert
					</Button>
					<Button onClick={handleOnRetry} isHollow disabled={retryDisabled || rephrasedData.inProgress}>
						Retry
					</Button>
				</div>
				{rephraseOptionsCount > 1 && (
					<IconButton
						icon={faTrashCan}
						onClick={onDismiss}
						isHollow
						square
						className="rounded border border-gray-300 p-2 transition hover:bg-gray-100"
					/>
				)}
			</div>

			<hr className="w-100 my-2 h-px border-none bg-neutral-2" />
		</div>
	);
};
